import React from "react";
import { Check, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import LoadFailure from "../../components/ui/LoadFailure";
import { useT, useLocalized } from "../../lib/LanguageContext";
import { platformMediaSrc } from "../../lib/platformMedia";
import { HQ_SITE } from "../../constants/testIds/hqSite";
import siteApi from "./siteApi";
import usePlatformMedia from "./usePlatformMedia";
import useSiteSchema from "./useSiteSchema";
import MediaUploadButton from "./MediaUploadButton";
import { siteErrorMessage } from "./siteErrors";

/**
 * Pick an image from the platform media library, or upload one and pick it in the same
 * step. Shared by every consumer of the library (contract §2.4): the landing page's image
 * fields, the HQ blog cover, plan images and booster images.
 *
 * Reads nothing until opened.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {(picked: { media_id: string, url: string }) => void} props.onSelect  called once;
 *   the picker closes itself afterwards.
 * @param {typeof siteApi} [props.api]
 */
export default function PlatformMediaPicker({ open, onClose, onSelect, api = siteApi }) {
  const t = useT("superadmin");
  const loc = useLocalized();
  const media = usePlatformMedia({ api, enabled: open });
  const schema = useSiteSchema({ api, enabled: open });

  const pick = (m) => {
    onSelect({ media_id: m.media_id, url: m.url });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid={HQ_SITE.picker}>
        <DialogHeader>
          <DialogTitle>{t("hsite.picker.title")}</DialogTitle>
          <DialogDescription>{t("hsite.picker.description")}</DialogDescription>
        </DialogHeader>

        {schema.status === "ready" && (
          <MediaUploadButton
            schema={schema.schema}
            upload={media.upload}
            onUploaded={pick}
            testId={HQ_SITE.pickerUpload}
          />
        )}

        <div className="overflow-y-auto flex-1 mt-2">
          {media.status === "loading" && (
            <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-amber-600" /></div>
          )}
          <LoadFailure
            message={media.status === "error" ? siteErrorMessage(media.error, t) : ""}
            onRetry={media.reload}
            retryLabel={t("hsite.retry")}
          />
          {media.status === "ready" && media.items.length === 0 && (
            <p className="py-12 text-center text-sm text-slate-400" data-testid={HQ_SITE.pickerEmpty}>
              {t("hsite.picker.empty")}
            </p>
          )}
          {media.status === "ready" && media.items.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {media.items.map((m) => {
                const alt = loc(m, "alt");
                return (
                  <button
                    key={m.media_id}
                    type="button"
                    onClick={() => pick(m)}
                    aria-label={t("hsite.picker.choose", { name: alt || m.original_name })}
                    data-testid={HQ_SITE.pickerItem(m.media_id)}
                    className="group relative aspect-square rounded-xl overflow-hidden border border-slate-200 hover:border-sol-gold focus:outline-none focus:ring-2 focus:ring-sol-gold/50"
                  >
                    <img src={platformMediaSrc(m.url)} alt="" className="w-full h-full object-cover" loading="lazy" />
                    <span className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition flex items-center justify-center text-white">
                      <Check className="w-5 h-5" />
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
